import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Product } from '../../types/Product';
import { NgbAlertModule, NgbTypeahead, NgbTypeaheadModule } from '@ng-bootstrap/ng-bootstrap';
import { CurrencyInputComponent } from './currency-input.component';
import { FormGroupComponent } from './form-group.component';
import { ProductService } from '../../services/products.service';
import { debounceTime, distinctUntilChanged, filter, firstValueFrom, lastValueFrom, map, Observable, OperatorFunction, Subject } from 'rxjs';
import { ResponseProductFilters } from '../../types/ResponseProductFilters';
import { ResponseOriginCode } from '../../types/dai-server/products/ResponseOriginCode';
import { response } from 'express';
import { LoadingComponent } from "../../../../node_modules/@digi-america/shared/src/lib/components/loading/loading.component";
import { ToastService } from '@digi-america/shared';

@Component({
    selector: 'app-product-editor',
    imports: [ReactiveFormsModule, NgbAlertModule, CurrencyInputComponent, FormGroupComponent, NgbTypeaheadModule],
    templateUrl: "./product-editor.component.html",
    styleUrl: "./product-editor.component.scss"
})
export class ProductEditorComponent implements OnInit {
    // Data input to receive the object.
    @Input() product!: Product;
    @Input() new!: boolean;

    @Output() productSaved = new EventEmitter<Product>();

    form!: FormGroup;
    originCodes!: ResponseOriginCode[];
    additionalAttrKeys: string[] = [];

    selectedFile: File | null = null;

    private productFilter: ResponseProductFilters | null = null;

    constructor(private fb: FormBuilder, private productService: ProductService, private toastService: ToastService) {
    }

    async ngOnInit() {
        if (!this.product) {
            console.error('No data provided to ProductEditorComponent.');
            return;
        }

        this.productService.getOriginCodes().subscribe({
            next: (response) => this.originCodes = response
        });

        this.productService.productFilters(this.product.type).subscribe({
            next: (response) => { this.productFilter = response }
        });

        // Extract keys for dynamic additional attributes.
        this.additionalAttrKeys = Object.keys(this.product.additionalAttributes || {});

        // Build dynamic form group for additionalAttributes.
        const additionalAttributesGroup: Record<string, FormGroup> = {};
        this.additionalAttrKeys.forEach(key => {
            const attr = this.product.additionalAttributes[key];
            additionalAttributesGroup[key] = this.fb.group({
                label: [attr.label],
                value: [attr.value, attr.required ? Validators.required : []],
                required: [attr.required]
            });
        });

        this.additionalAttrKeys.sort((a, b) => this.product.additionalAttributes[a].order - this.product.additionalAttributes[b].order)

        // Build the main form.
        this.form = this.fb.group({
            id: new FormControl(this.product.id),
            type: new FormControl(this.product.type),
            sku: new FormControl(this.product.sku, [Validators.required, Validators.minLength(3), Validators.maxLength(32)]),
            name: new FormControl(this.product.name, [Validators.required, Validators.minLength(3), Validators.maxLength(60)]),
            active: new FormControl(this.product.active),
            // Always sent back on update, the server resets the minimum to 1 when it is left out
            minimumOrderQuantity: new FormControl(this.product.minimumOrderQuantity ?? 1, [Validators.required, Validators.min(1), Validators.pattern(/^\d+$/)]),
            description: new FormControl(this.product.description, Validators.maxLength(255)),
            memo: new FormControl(this.product.memo, Validators.maxLength(255)),
            originCode: new FormControl(this.product.originCode, Validators.required),
            listPrice: new FormControl(this.product.listPrice ?? 0, [Validators.required, Validators.min(0.00)]),
            dealerPrice: new FormControl(this.product.dealerPrice ?? 0, [Validators.required, Validators.min(0.00)]),
            directPrice: new FormControl(this.product.directPrice ?? 0, [Validators.required, Validators.min(0.00)]),
            additionalAttributes: this.fb.group(additionalAttributesGroup)
        });
    }

    onFileSelected(event: Event): void {
        const maxSize = 1024 * 1024 * 20;
        const input = event.target as HTMLInputElement;

        // Clear an existing file
        this.selectedFile = null;
        if (input.files && input.files.length > 0) {
            const file = input.files[0];
            if (file.size > maxSize) {
                this.toastService.error("Max file size is 20MB.");
            }
            else {
                this.selectedFile = file;
            }
        }
    }

    async onSubmit(): Promise<void> {
        this.form.markAllAsTouched();
        if (this.form.valid) {
            let productForm = this.form.getRawValue();

            if (this.selectedFile) {
                await lastValueFrom(this.productService
                    .uploadProductImage(this.selectedFile, productForm.type, productForm.sku));
            }

            this.productSaved.emit(productForm);
        }
        else {
            this.toastService.error("One of more value(s) need adjustments.");
        }
    }

    searchFor(key: string): OperatorFunction<string, readonly string[]> {
        return (text$: Observable<string>) => {
            let values: string[] = [];
            if (this.productFilter) {
                const attrFilters = this.productFilter.filters.find(f => f.key == key);
                if (attrFilters) {
                    values = attrFilters.options.map(o => o.value);
                }
            }

            return text$.pipe(
                map((term) =>
                    values.filter((v) => v.toLowerCase().indexOf(term.toLowerCase()) > -1).slice(0, 10)
                ),
            );
        };
    }
}